import {
  keepPreviousData,
  useMutation,
  useQuery,
} from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { Auth } from '@mint-sdk/api-auth';
import { type TagBffType } from '@mint-sdk/api-tags/compat';

import useConditionalSearch from '../../hooks/useConditionalSearch/useConditionalSearch.js';
import useTab, { RouteTabs } from '../../hooks/useTab/useTab.js';
import useTablePagination from '../../hooks/useTablePagination/useTablePagination.jsx';
import useTableSorting from '../../hooks/useTableSorting/useTableSorting.jsx';
import type { WithPaginationResponse } from '../types.js';
import {
  createUser as createUserReq,
  deleteUser as deleteUserReq,
  editUser as editUserReq,
  fetchUsers,
  resendUserInvitation as resendUserInvitationReq,
  toggleUserActivityStatus as toggleUserActivityStatusReq,
  type UserType,
} from '../users/index.js';
import { checkEmail, checkEmailNew } from '../users/users.js';
import { queryClient, QueryKey } from './main.js';

type Params = {
  getFullList?: boolean;
  // in case of modal use fro useUsers
  disableUsersFetch?: boolean;
  tags?: TagBffType[];
};

export const useUsers = (params?: Params) => {
  const searchParams = useConditionalSearch();

  const ff = useFlags('@mint/arm', ['userV2Endpoint']);
  const { limit, offset } = useTablePagination();

  const { getFullList, disableUsersFetch } = params ?? {};

  const { orderingBE } = useTableSorting();

  const searchString = !getFullList ? searchParams?.search : '';

  const tab = useTab();

  const users = useQuery({
    queryKey: [
      QueryKey.UsersData,
      searchString,
      limit,
      offset,
      orderingBE,
      getFullList,
    ],
    queryFn: () =>
      fetchUsers({
        search: searchString,
        limit: getFullList ? 0 : limit,
        offset: offset,
        ordering: orderingBE,
      }),

    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled:
      !ff.userV2Endpoint.enabled &&
      !disableUsersFetch &&
      (tab === RouteTabs.User || Boolean(getFullList)),
  });

  const paginatedUsers = useQuery({
    ...Auth.listPaginatedUsers({
      params: {
        search: searchString,
        limit: limit,
        offset,
        ordering: orderingBE,
        includeInheritedTags: true,
      },
    }),

    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled:
      ff.userV2Endpoint.enabled &&
      !disableUsersFetch &&
      tab === RouteTabs.User &&
      !getFullList &&
      limit !== 0,
  });

  const allUsers = useQuery({
    ...Auth.listAllUsers({
      params: {
        search: searchString,
        limit: 0,
        offset,
        ordering: orderingBE,
      },
    }),

    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled:
      ff.userV2Endpoint.enabled &&
      !disableUsersFetch &&
      tab === RouteTabs.Role &&
      (Boolean(getFullList) || limit === 0),
  });

  const createUser = useMutation({
    mutationFn: createUserReq,
  });

  const editUser = useMutation({
    mutationFn: editUserReq,
  });

  const toggleUserActivityStatus = useMutation({
    mutationFn: toggleUserActivityStatusReq,
  });

  const deleteUser = useMutation({
    mutationFn: deleteUserReq,
  });

  const resendUserInvitation = useMutation({
    mutationFn: resendUserInvitationReq,
  });

  const refetchUsers = () => {
    if (ff.userV2Endpoint.enabled) {
      return queryClient.invalidateQueries(
        Auth.listPaginatedUsers({
          params: {},
        }),
      );
    }
    return queryClient.invalidateQueries({
      queryKey: [QueryKey.UsersData],
    });
  };

  const checkUserByEmail = useMutation({ mutationFn: checkEmail });
  const checkUserByEmailNew = useMutation({ mutationFn: checkEmailNew });

  return {
    users: ff.userV2Endpoint.enabled
      ? paginatedUsers
      : {
          ...users,
          data: users.data as WithPaginationResponse<UserType>,
        },
    allUsers: ff.userV2Endpoint.enabled
      ? allUsers
      : {
          ...users,
          data: users.data as UserType[],
        },
    createUser,
    editUser,
    deleteUser,
    resendUserInvitation,
    toggleUserActivityStatus,
    refetchUsers,
    checkUserByEmail,
    checkUserByEmailNew,
  };
};
